import React, { useEffect, useState } from 'react';
import {
  Box,
  Text,
  HStack,
  SimpleGrid,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
} from '@chakra-ui/react';
import type { MarketplaceProfileTurnaroundPreset } from '@foodfluence/shared';
import {
  encodeTurnaroundCustom,
  isTurnaroundOtherUi,
  isTurnaroundPreset,
  parseTurnaroundCustom,
  type TurnaroundCustomParts,
} from '../../lib/turnaroundUtils';
import { ff } from '../../../../../../theme';

const PRESET_OPTIONS: { id: MarketplaceProfileTurnaroundPreset; label: string }[] = [
  { id: '24h', label: '24 hours' },
  { id: '72h', label: '72 hours' },
  { id: '1w', label: '1 week' },
];

// Selected chip: ink fill + lime text.
const chipStyles = (selected: boolean) => ({
  h: '48px',
  display: 'flex' as const,
  alignItems: 'center' as const,
  justifyContent: 'center' as const,
  borderRadius: '14px',
  border: '1px solid',
  borderColor: selected ? ff.ink : ff.border,
  bg: selected ? ff.ink : 'white',
  color: selected ? ff.lime : ff.ink,
  fontFamily: 'display',
  fontSize: '14px',
  fontWeight: selected ? 600 : 500,
  _hover: selected ? undefined : { borderColor: ff.ink },
});

export interface TurnaroundTimeSectionProps {
  value: string | null;
  onChange: (turnaroundTime: string) => void;
}

const TurnaroundTimeSection: React.FC<TurnaroundTimeSectionProps> = ({ value, onChange }) => {
  /**
   * “Other” can encode to values that *look like* presets (e.g. `1w`).
   * We keep a UI flag so the “Other” inputs don't collapse unexpectedly.
   */
  const [otherUiSelected, setOtherUiSelected] = useState(false);
  const isOther = otherUiSelected ? true : isTurnaroundOtherUi(value);
  const parsedCustom = value && isOther ? parseTurnaroundCustom(value) : null;

  const [otherAmount, setOtherAmount] = useState(() =>
    String(parsedCustom?.amount ?? 1)
  );
  const [otherUnit, setOtherUnit] = useState<TurnaroundCustomParts['unit']>(
    () => parsedCustom?.unit ?? 'days'
  );

  useEffect(() => {
    if (!isOther || !value) return;
    const parsed = parseTurnaroundCustom(value);
    if (parsed) {
      setOtherAmount(String(parsed.amount));
      setOtherUnit(parsed.unit);
    }
  }, [value, isOther]);

  const emitOther = (amountStr: string, unit: TurnaroundCustomParts['unit']) => {
    setOtherUiSelected(true);
    const parsed = parseInt(amountStr, 10);
    const amount = Number.isFinite(parsed) && parsed >= 1 ? parsed : 1;
    onChange(encodeTurnaroundCustom({ amount, unit }));
  };

  const selectPreset = (id: MarketplaceProfileTurnaroundPreset) => {
    setOtherUiSelected(false);
    onChange(id);
  };

  const selectOther = () => {
    setOtherUiSelected(true);
    const parsed = parseInt(otherAmount, 10);
    const amount = Number.isFinite(parsed) && parsed >= 1 ? parsed : 1;
    onChange(encodeTurnaroundCustom({ amount, unit: otherUnit }));
  };

  return (
    <Box as="section" aria-label="Turnaround time">
      <HStack justify="space-between" align="center" mb="10px" spacing={2}>
        <Text as="h3" textStyle="eyebrow" color={ff.text2} flex={1} m={0}>
          Turnaround time
        </Text>
        <Text fontSize="12px" lineHeight="17px" color={ff.muted} textAlign="right" flexShrink={0}>
          Shown on your profile
        </Text>
      </HStack>
      <Box
        border="1px solid"
        borderColor={ff.line}
        borderRadius="20px"
        overflow="hidden"
        bg="white"
      >
        <Box p="12px" bg="white">
          <SimpleGrid columns={3} spacing="8px" mb="8px">
            {PRESET_OPTIONS.map((opt) => {
              const selected =
                otherUiSelected === false &&
                value !== null &&
                isTurnaroundPreset(value) &&
                value === opt.id;
              return (
                <Box
                  key={opt.id}
                  as="button"
                  type="button"
                  onClick={() => selectPreset(opt.id)}
                  {...chipStyles(selected)}
                >
                  {opt.label}
                </Box>
              );
            })}
          </SimpleGrid>

          <Box
            as="button"
            type="button"
            w="100%"
            onClick={selectOther}
            {...chipStyles(isOther)}
          >
            Other
          </Box>

          {isOther && (
            <HStack mt="10px" spacing="8px" align="center">
              <Box flex={1}>
                <NumberInput
                  min={1}
                  max={52}
                  value={otherAmount}
                  onChange={(_, n) => {
                    const next = Number.isFinite(n) && n >= 1 ? String(Math.floor(n)) : '1';
                    setOtherAmount(next);
                    emitOther(next, otherUnit);
                  }}
                >
                  <NumberInputField
                    h="48px"
                    borderRadius="14px"
                    borderColor={ff.border}
                    bg="white"
                    fontSize="16px"
                    _hover={{ borderColor: ff.muted }}
                    _focusVisible={{ borderColor: ff.ink, boxShadow: `0 0 0 1px ${ff.ink}` }}
                  />
                  <NumberInputStepper>
                    <NumberIncrementStepper />
                    <NumberDecrementStepper />
                  </NumberInputStepper>
                </NumberInput>
              </Box>
              <Box flex={1}>
                <HStack spacing="6px">
                  {(['days', 'weeks'] as const).map((unit) => {
                    const unitSelected = otherUnit === unit;
                    return (
                      <Box
                        key={unit}
                        as="button"
                        type="button"
                        flex={1}
                        h="48px"
                        onClick={() => {
                          setOtherUnit(unit);
                          emitOther(otherAmount, unit);
                        }}
                        borderRadius="14px"
                        border="1px solid"
                        borderColor={unitSelected ? ff.ink : ff.border}
                        bg={unitSelected ? ff.ink : 'white'}
                        color={unitSelected ? ff.lime : ff.ink}
                        fontFamily="display"
                        fontSize="14px"
                        fontWeight={unitSelected ? 600 : 500}
                        textTransform="capitalize"
                      >
                        {unit}
                      </Box>
                    );
                  })}
                </HStack>
              </Box>
            </HStack>
          )}
        </Box>
        <Text
          fontSize="13px"
          lineHeight="1.4"
          color={ff.text2}
          borderTop="1px solid"
          borderColor={ff.line}
          bg={ff.grey}
          py="10px"
          px="16px"
        >
          Brands see this before they order so expectations stay aligned.
        </Text>
      </Box>
    </Box>
  );
};

export default TurnaroundTimeSection;
